// LandingView — v2 front door: choose a source (upload / live bridge / example).
//
// Copy teaches what Facet is; the three cards map 1:1 to the three sources in
// scenarioSourceSlice — and per the v2 design the cards ARE the source UX:
// the upload card carries the dropzone, the live card carries the connect
// controls (the old UploadDrawer is gone). Example-card stats are derived
// from the loaded example manifest — never hard-coded counts.

import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store/index.ts';
import { setActiveSource } from '../../store/slices/scenarioSourceSlice.ts';
import { clearSuiteResults } from '../../store/slices/simulationSlice.ts';
import { setAppView } from '../../store/slices/uiSlice.ts';
import { classifyScenarioType, KIND_GLYPHS } from '../simulation/scenarioBadges.ts';
import { LEDGER_KIND_ORDER } from './ledgerData.ts';
import UploadDropzone from './UploadDropzone.tsx';
import LiveConnectForm from './LiveConnectForm.tsx';

export default function LandingView() {
  const dispatch = useDispatch();
  const example = useSelector((s: RootState) => s.scenarioSource.example);
  const user = useSelector((s: RootState) => s.scenarioSource.user);
  const ws = useSelector((s: RootState) => s.scenarioSource.websocket);
  const activeSource = useSelector((s: RootState) => s.scenarioSource.activeSource);

  const exampleManifest = example.manifest;
  const exampleFlows = exampleManifest?.flows.length ?? null;
  const exampleEntries = exampleManifest?.flows.flatMap((f) => f.scenarios) ?? [];
  const kindCounts: Partial<Record<string, number>> = {};
  for (const e of exampleEntries) {
    const k = classifyScenarioType(e);
    kindCounts[k] = (kindCounts[k] ?? 0) + 1;
  }
  const kindLine = LEDGER_KIND_ORDER.filter((k) => kindCounts[k])
    .map((k) => `${KIND_GLYPHS[k]}${kindCounts[k]}`)
    .join(' ');

  const openSource = (source: 'example' | 'user' | 'websocket') => {
    // Suite rows are keyed by scenarioId of the manifest that produced them;
    // when the SOURCE changes they are stale truth and must be dropped so the
    // portfolio ledger auto-replays for the newly active source.
    if (source !== activeSource) dispatch(clearSuiteResults());
    dispatch(setActiveSource(source));
    dispatch(setAppView('portfolio'));
  };

  return (
    <div className="fv2-page" data-screen="landing">
      <div className="fv2-page-inner fv2-landing">
        <div className="fv2-kicker">THE PROOF HARNESS FOR MOMENT DESIGNS</div>
        <h1 className="fv2-hero-title">
          Prove your domain design behaves — before anything is built.
        </h1>
        <p className="fv2-hero-copy">
          Facet loads the artifacts the <code>moment</code> CLI generates and replays every scenario
          through a real assertion engine — seven proof tiers per run. What you get back is a
          verdict, a story, and evidence. Never a green it didn&rsquo;t earn.
        </p>

        <div className="fv2-source-grid">
          {/* ── Upload ── */}
          <div className="fv2-source-card fv2-source-upload">
            <div className="fv2-card-kicker" style={{ color: 'var(--accent)' }}>
              ⬆ UPLOAD ARTIFACTS
            </div>
            <div className="fv2-card-title">Drop your design&rsquo;s files</div>
            <div className="fv2-card-mono-list">
              <span>
                ▸ *-topology.json <em>— lanes, moments, contracts</em>
              </span>
              <span>
                ▸ *-scenario-*.json <em>— every walkable path + events</em>
              </span>
              <span>
                ▸ saga / policy defs <em>— optional, +2 proof tiers</em>
              </span>
            </div>
            <p className="fv2-card-note">
              Older artifact shapes load too — unverifiable tiers become labeled skips, never
              silence.
            </p>
            <UploadDropzone />
            {user.manifest && (
              <button className="fv2-card-btn fv2-btn-accent" onClick={() => openSource('user')}>
                OPEN MY PROJECT →
              </button>
            )}
          </div>

          {/* ── Live bridge ── */}
          <div className="fv2-source-card">
            <div className="fv2-card-kicker" style={{ color: 'var(--green)' }}>
              ● LIVE BRIDGE
            </div>
            <div className="fv2-card-title">Iterate against a running spec</div>
            <div className="fv2-card-mono-list">
              <span className="fv2-dim"># in your Moment project</span>
              <span>
                $ <b>npm i -g @moment/cli</b>
              </span>
              <span>
                $ <b>moment serve --spec your-design.moment</b>
              </span>
              <span className="fv2-dim">→ serving on ws://localhost:7877</span>
            </div>
            <p className="fv2-card-note">
              Facet watches the socket. Every save re-parses, re-derives, and re-proves your design
              — the canvas updates live.
            </p>
            <LiveConnectForm />
            {ws.connectionStatus === 'connected' && ws.manifest && (
              <button
                className="fv2-card-btn fv2-btn-green"
                onClick={() => openSource('websocket')}
              >
                OPEN LIVE SESSION →
              </button>
            )}
          </div>

          {/* ── Bundled example ── */}
          <div className="fv2-source-card">
            <div className="fv2-card-kicker" style={{ color: 'var(--blue)' }}>
              ◇ BUNDLED EXAMPLE
            </div>
            <div className="fv2-card-title">Learn how Facet reads</div>
            <p className="fv2-card-copy">
              <b>TicketWave</b> is a fictitious ticketing business designed in Moment — flows,
              designed failures, refusals, and saga timeouts, all generated from one spec.
            </p>
            {exampleManifest ? (
              <div className="fv2-card-stats">
                <span>
                  <b>{exampleFlows}</b> flows
                </span>
                <span>
                  <b>{exampleEntries.length}</b> scenarios
                </span>
                {kindLine && <span className="fv2-kindline">{kindLine}</span>}
                <span>
                  <b>7</b> proof tiers
                </span>
              </div>
            ) : (
              <div className="fv2-card-stats fv2-dim">
                {example.loading ? 'loading the example manifest…' : 'example manifest not loaded'}
              </div>
            )}
            <p className="fv2-card-note">
              The fastest way to see verdicts, stories, and evidence on a design that isn&rsquo;t
              yours yet.
            </p>
            <button
              className="fv2-card-btn fv2-btn-outline"
              onClick={() => openSource('example')}
              disabled={!exampleManifest}
            >
              LOAD TICKETWAVE →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
